import { useState } from 'react';
import { Button, RG, RadioGroupOption } from '@crazyegginc/hatch';

import { useModal } from '/src/hooks';
import { Modal } from '/src/contexts/modal';
import { SlackModal } from './SlackModal';
import { EmailModal } from './EmailModal';
import { APIModal } from './APIModal';
import { ZapierModal } from './ZapierModal';

import { ReactComponent as SlackIcon } from '@crazyegginc/hatch/dist/images/logo-slack.svg';
import { ReactComponent as ZapierIcon } from '@crazyegginc/hatch/dist/images/logo-zapier-integrations.svg';
import { ReactComponent as MailIcon } from '@crazyegginc/hatch/dist/images/icon-mail.svg';
import { ReactComponent as CodeIcon } from '@crazyegginc/hatch/dist/images/icon-code-outline.svg';
import { Pill } from '/src/components/Pill';
import { ACTION_META, RESOURCE_META } from '../../constants';

export function GetNotifiedModal({ resourceType, resourceId, action = null, BackToModal }) {
  const modal = useModal();
  const [selectedAction, setSelectedAction] = useState(action ?? RESOURCE_META[resourceType].defaultAction);
  const { actions: availableActions, actionTextFn } = RESOURCE_META[resourceType];
  const fallbackActionText = actionTextFn();

  return (
    <Modal>
      <Modal.Title>Get Notified</Modal.Title>

      {availableActions.length > 1 ? (
        <>
          <p className="text-header-5">Notify me when...</p>

          <RG value={selectedAction} onChange={(newValue) => setSelectedAction(newValue)}>
            <div className="flex flex-col -ml-1 mt-1 mb-1 space-y-0.5 rounded-full">
              {availableActions.map((a) => (
                <RadioGroupOption
                  key={`ACTION_${a}`}
                  size="sm"
                  labelClass="!ml-1.25"
                  option={{
                    value: a,
                    label: (
                      <div className="flex items-center space-x-2.5">
                        <span>{ACTION_META[a].label}</span>
                        {ACTION_META[a].pill ? (
                          <Pill className="bg-[#A15EEE] text-white h-5 !border-none leading-3 text-2xs font-semibold">
                            {ACTION_META[a].pill}
                          </Pill>
                        ) : null}
                      </div>
                    ),
                  }}
                />
              ))}
            </div>
          </RG>
        </>
      ) : (
        <p className="text-body-2">We’ll send you a notification {fallbackActionText}.</p>
      )}

      <p className="text-header-5 mt-2">Where would you like to be notified?</p>

      <Modal.Actions className="!mt-5 flex flex-col !items-start space-y-2.5 !space-x-0">
        <div className="flex space-x-2.5">
          <Button
            variant="secondary"
            onClick={() =>
              modal.show(
                <SlackModal
                  resourceType={resourceType}
                  resourceId={resourceId}
                  action={selectedAction}
                  BackToModal={GetNotifiedModal}
                  closeAfterAction={true}
                />,
              )
            }
          >
            <SlackIcon className="mr-2 h-4 w-4 shrink-0" />
            Slack
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              modal.show(
                <EmailModal
                  resourceType={resourceType}
                  resourceId={resourceId}
                  action={selectedAction}
                  BackToModal={GetNotifiedModal}
                  closeAfterAction={true}
                />,
              )
            }
          >
            <MailIcon className="mr-2 h-4 w-4 shrink-0 fill-current" />
            Email
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              modal.show(
                <APIModal
                  resourceType={resourceType}
                  resourceId={resourceId}
                  action={selectedAction}
                  BackToModal={GetNotifiedModal}
                  closeAfterAction={true}
                />,
              )
            }
          >
            <CodeIcon className="mr-2 h-4 w-4 shrink-0 fill-current" />
            API
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              modal.show(
                <ZapierModal
                  resourceType={resourceType}
                  resourceId={resourceId}
                  action={selectedAction}
                  BackToModal={GetNotifiedModal}
                />,
              )
            }
          >
            <ZapierIcon className="mr-2 h-4 w-4 shrink-0" />
            Zapier
          </Button>
        </div>
        {BackToModal ? (
          <div className="flex items-center">
            <Button
              variant="cancel"
              onClick={() =>
                BackToModal
                  ? modal.show(
                      <BackToModal resourceType={resourceType} resourceId={resourceId} action={selectedAction} />,
                    )
                  : modal.close()
              }
              className="disabled:bg-transparent"
            >
              Cancel
            </Button>
          </div>
        ) : null}
      </Modal.Actions>
    </Modal>
  );
}
